import type { components as apiComponents } from '@/lib/api/types'
import { client } from './client'
import { getCsrf, getCookie } from './authentication'
import { CustomError } from '../CustomError'
import { CSRF_TOKEN_NAME } from '@/constants'

type CreateUserDefaultThemeRequestBody =
  apiComponents['schemas']['DocumentPageThemeUserDefaultNewIn']

type UpdateUserDefaultThemeRequestBody =
  apiComponents['schemas']['DocumentPageThemeUserDefaultUpdateIn']

export const createDocumentPageThemeUserDefault = async (
  values: CreateUserDefaultThemeRequestBody,
  jwtToken?: string | null,
) => {
  await getCsrf()

  const headers = {
    'X-CSRFToken': getCookie(CSRF_TOKEN_NAME),
  }

  if (jwtToken) {
    headers['Authorization'] = `Bearer ${jwtToken}`
  }

  const resp = await client.POST(
    '/api/v1/projects/document_page_theme_user_defaults',
    {
      headers,
      body: values,
    },
  )

  if (resp.error || !resp.response.ok) {
    const message = 'Failed to create document page theme'
    throw new CustomError(resp, message)
  }

  return resp.data
}

export const listDocumentPageThemeUserDefaults = async (
  jwtToken?: string | null,
) => {
  const headers = {}

  if (jwtToken) {
    headers['Authorization'] = `Bearer ${jwtToken}`
  }

  const resp = await client.GET(
    '/api/v1/projects/document_page_theme_user_defaults',
    {
      headers,
    },
  )

  if (resp.error || !resp.response.ok) {
    const message = 'Failed to list document page theme user defaults'
    throw new CustomError(resp, message)
  }

  return resp.data
}

export const updateDocumentPageThemeUserDefault = async (
  userThemeDefaultId,
  values: UpdateUserDefaultThemeRequestBody,
  jwtToken?: string | null,
) => {
  await getCsrf()

  const headers = {
    'X-CSRFToken': getCookie(CSRF_TOKEN_NAME),
  }

  if (jwtToken) {
    headers['Authorization'] = `Bearer ${jwtToken}`
  }

  const resp = await client.PUT(
    '/api/v1/projects/document_page_theme_user_defaults/{document_page_theme_user_default_id}',
    {
      headers,
      params: {
        path: {
          document_page_theme_user_default_id: userThemeDefaultId,
        },
      },
      body: values,
    },
  )

  if (resp.error || !resp.response.ok) {
    const message = 'Failed to update document page theme user default'
    throw new CustomError(resp, message)
  }

  return resp.data
}

export const deleteDocumentPageThemeUserDefault = async (
  userThemeDefaultId,
) => {
  await getCsrf()
  const resp = await client.DELETE(
    '/api/v1/projects/document_page_theme_user_defaults/{document_page_theme_user_default_id}',
    {
      headers: {
        'X-CSRFToken': getCookie(CSRF_TOKEN_NAME),
      },
      params: {
        path: {
          document_page_theme_user_default_id: userThemeDefaultId,
        },
      },
      parseAs: 'text',
    },
  )

  if (resp.error || !resp.response.ok) {
    const message = 'Failed to delete document page theme user default'
    throw new CustomError(resp, message)
  }

  return true
}
